import { useMemo, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import type { ColumnFiltersState } from '@tanstack/react-table'
import type { IUser } from '@/types/user'
import { getLocalStorageKey, safeGetFromLocalStorage, safeSetToLocalStorage } from '@/lib/utils/localStorage'
import { useSession } from '@/contexts/SessionProvider'
import log from '@/providers/log'

interface IUseSavedFiltersReturn {
  initialColumnFilters: ColumnFiltersState
  initialSearch: string
  saveSearch: (search: string) => void
  saveColumnFilters: (columnFilters: ColumnFiltersState) => void
}

interface ISavedOption {
  attribute: string
  values: string[]
}
interface ISavedFilter {
  search: string
  selectedOptions?: ISavedOption[]
}

const INITIAL_FILTERS = {
  initialColumnFilters: [],
  initialSearch: '',
}

export function getTenantPrefix(user: IUser) {
  return `tableFilter-${user.simulatedOrganization?.id ?? user.organization.id}`
}

// We need to keep this two functions for backwards compatibility for now with the current logic elsewehre
const savedOptionsToColumnFilters = (savedOptions: ISavedOption[]) => {
  return savedOptions.map((option) => ({
    id: option.attribute,
    value: option.values,
  }))
}

const columnFiltersToSavedOptions = (columnFilters: ColumnFiltersState): ISavedOption[] => {
  return columnFilters.map((filter) => ({
    attribute: filter.id,
    values: Array.isArray(filter.value) ? filter.value : [filter.value],
  }))
}

function useRetrieveSavedFilters(pathname: string, tenantPrefix: string) {
  const { initialColumnFilters, initialSearch } = useMemo(() => {
    const storageKey = getLocalStorageKey(pathname, tenantPrefix)
    const savedFilters = safeGetFromLocalStorage(storageKey)

    if (!savedFilters) return { ...INITIAL_FILTERS }
    try {
      const parsed = JSON.parse(savedFilters) as ISavedFilter
      const savedOptions = parsed.selectedOptions || []
      const columnFilters = savedOptionsToColumnFilters(savedOptions)

      return {
        initialColumnFilters: columnFilters,
        initialSearch: parsed.search || '',
      }
    } catch (error) {
      log.error(`Error getting saved table filters, using default values ${String(error)}`)
      return { ...INITIAL_FILTERS }
    }
  }, [pathname, tenantPrefix])

  return { initialColumnFilters, initialSearch }
}

export function useSavedTableFilters(): IUseSavedFiltersReturn {
  const rawPathname = usePathname()
  const { user } = useSession()
  const tenantPrefix = getTenantPrefix(user)

  // @TODO: Quick fix so this works when redirecting with old/new controls
  const pathname = rawPathname === '/compliance/controls' ? '/compliance/controls' : rawPathname

  const { initialColumnFilters, initialSearch } = useRetrieveSavedFilters(pathname, tenantPrefix)

  const saveSearch = useCallback(
    (search: string) => {
      const storageKey = getLocalStorageKey(pathname, tenantPrefix)
      const savedFilters = safeGetFromLocalStorage(storageKey)
      let currentData = {}

      if (savedFilters) {
        try {
          currentData = JSON.parse(savedFilters) as ISavedFilter
        } catch (error) {
          log.error(`Error saving search filter in local storage: ${String(error)}`)
        }
      }
      safeSetToLocalStorage(storageKey, JSON.stringify({ ...currentData, search }))
    },
    [pathname, tenantPrefix],
  )

  const saveColumnFilters = useCallback(
    (columnFilters: ColumnFiltersState) => {
      const storageKey = getLocalStorageKey(pathname, tenantPrefix)
      const savedFilters = safeGetFromLocalStorage(storageKey)
      let currentData = {}

      if (savedFilters) {
        try {
          currentData = JSON.parse(savedFilters) as ISavedFilter
        } catch (error) {
          log.error(`Error saving column filter in local storage: ${String(error)}`)
        }
      }
      const selectedOptions = columnFiltersToSavedOptions(columnFilters)
      safeSetToLocalStorage(storageKey, JSON.stringify({ ...currentData, selectedOptions }))
    },
    [pathname, tenantPrefix],
  )

  return { initialColumnFilters, initialSearch, saveSearch, saveColumnFilters }
}
